import type { CreateQuestionRequest } from '@e-learning/core';
import type { CreateQuestionFormData } from '@e-learning/shared';
import {
  CREATE_QUESTION_MIN_OPTIONS,
  CreateCourseHeader,
  CreateQuestionForm,
  iconArrowLeftHeaderCourses,
  Toast,
  useTheme,
} from '@e-learning/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useCourses } from '../Courses/hooks';
import { useQuestions } from './hooks';


interface CourseCategoryItem {
  id: string;
  translations: { languageCode: string; name: string }[];
}

function formDataToCreateQuestionRequest(
  data: CreateQuestionFormData
): CreateQuestionRequest {
  const translations = [
    {
      languageCode: 'en-US',
      title: (data.questionText?.en ?? '').trim() || '',
    },
    {
      languageCode: 'es-ES',
      title: (data.questionText?.es ?? '').trim() || '',
    },
  ].filter(t => t.title !== '');
  if (translations.length === 0) {
    translations.push({ languageCode: 'en-US', title: '' });
  }

  const options = (data.options ?? []).map(opt => [
    { languageCode: 'en-US' as const, text: (opt?.en ?? '').trim() || '' },
    { languageCode: 'es-ES' as const, text: (opt?.es ?? '').trim() || '' },
  ]);

  const explanation =
    (data.explanation?.en?.trim() || data.explanation?.es?.trim()) &&
    [
      { languageCode: 'en-US', text: (data.explanation?.en ?? '').trim() },
      { languageCode: 'es-ES', text: (data.explanation?.es ?? '').trim() },
    ].filter(e => e.text !== '');

  return {
    translations,
    difficultyLevel: parseInt(data.difficultyLevel, 10) || 0,
    courseCategoryId: data.categoryId?.trim() ?? '',
    linkedCourseIds:
      data.linkedCourseIds?.length > 0 ? data.linkedCourseIds : undefined,
    options,
    correctOptionIndex: Math.max(
      0,
      Math.min(data.correctOptionIndex, options.length - 1)
    ),
    explanation:
      explanation && explanation.length > 0 ? explanation : undefined,
  };
}

const initialFormData: CreateQuestionFormData = {
  questionText: { en: '', es: '' },
  difficultyLevel: '',
  categoryId: '',
  linkedCourseIds: [],
  options: Array.from({ length: CREATE_QUESTION_MIN_OPTIONS }, () => ({
    en: '',
    es: '',
  })),
  correctOptionIndex: 0,
  explanation: { en: '', es: '' },
};

export default function CreateQuestionBankPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { createQuestion } = useQuestions();
  const { fetchCourseCategories, fetchCourses } = useCourses();

  const [formData, setFormData] =
    useState<CreateQuestionFormData>(initialFormData);
  const [saving, setSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState<{
    text: string;
    variant: 'success' | 'error';
  } | null>(null);
  const [categoryList, setCategoryList] = useState<CourseCategoryItem[]>([]);
  const [courseList, setCourseList] = useState<{ id: string; name: string }[]>(
    []
  );

  const difficultyOptions = useMemo(
    () => [
      { value: '0', label: t('questionBank.create.difficulty_low') },
      { value: '1', label: t('questionBank.create.difficulty_medium') },
      { value: '2', label: t('questionBank.create.difficulty_high') },
    ],
    [t]
  );
  const categoryOptions = useMemo(
    () =>
      categoryList.map(c => ({
        value: c.id,
        label: c.translations?.[0]?.name ?? c.id,
      })),
    [categoryList]
  );
  const courseOptions = useMemo(
    () =>
      courseList.map(c => ({
        value: c.id,
        label: c.name || c.id,
      })),
    [courseList]
  );

  useEffect(() => {
    let cancelled = false;
    fetchCourseCategories({ includeInactive: false })
      .then((list: CourseCategoryItem[]) => {
        if (!cancelled) setCategoryList(list);
      })
      .catch(() => {
        if (!cancelled) setCategoryList([]);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchCourseCategories]);

  useEffect(() => {
    let cancelled = false;
    fetchCourses({ page: 1, limit: 200 })
      .then(res => {
        if (!cancelled && res?.data)
          setCourseList(
            res.data.map((c: { id: string; name?: string }) => ({
              id: c.id,
              name: c.name ?? c.id,
            }))
          );
      })
      .catch(() => {
        if (!cancelled) setCourseList([]);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchCourses]);

  useEffect(() => {
    if (!toastMessage) return;
    const id = setTimeout(() => setToastMessage(null), 4000);
    return () => clearTimeout(id);
  }, [toastMessage]);

  const handleSave = useCallback(
    async (data: CreateQuestionFormData) => {
      setSaving(true);
      setToastMessage(null);
      try {
        const request = formDataToCreateQuestionRequest(data);
        await createQuestion(request);
        setToastMessage({
          text: t('questionBank.create.save_success'),
          variant: 'success',
        });
        navigate('/admin/question-bank');
      } catch {
        setToastMessage({
          text: t('questionBank.create.save_error'),
          variant: 'error',
        });
      } finally {
        setSaving(false);
      }
    },
    [createQuestion, navigate, t]
  );

  const handleCancel = useCallback(() => {
    navigate('/admin/question-bank');
  }, [navigate]);

  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
  };

  const mainStyle: React.CSSProperties = {
    maxWidth: theme.layout.contentMaxWidth,
    margin: '0 auto',
    padding: theme.spacing.lg,
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius.md,
    padding: theme.spacing.xl,
    marginBottom: theme.spacing.xl,
  };

  return (
    <div style={pageStyle}>
      {toastMessage && (
        <Toast
          message={toastMessage.text}
          variant={toastMessage.variant}
          role="alert"
        />
      )}
      <CreateCourseHeader
          backLink={
          <Link
            to="/admin/question-bank"
            style={{
              color: 'inherit',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: theme.spacing.sm,
            }}
          >
            <img
              src={iconArrowLeftHeaderCourses}
              alt=""
              width={theme.palette.createCourse.addModuleIconSize}
              height={theme.palette.createCourse.addModuleIconSize}
              aria-hidden
              style={{ display: 'block' }}
            />
            {t('questionBank.create.back_to_question_bank')}
          </Link>
        }
      />
      <main style={mainStyle}>
        <div style={cardStyle}>
          <h1
            style={{
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.fontSize.xl,
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.palette.text.primary,
              margin: 0,
              marginBottom: theme.spacing.md,
            }}
          >
            {t('questionBank.create.modal_title')}
          </h1>
          <CreateQuestionForm
            data={formData}
            onChange={setFormData}
            difficultyOptions={difficultyOptions}
            categoryOptions={categoryOptions}
            courseOptions={courseOptions}
            onCancel={handleCancel}
            onSave={handleSave}
            saving={saving}
          />
        </div>
      </main>
    </div>
  );
}
